import { useState, useRef, useEffect } from "react";
import assemblyLogo from "@/assets/AssemblyByQuantumWorkplace_Horiz-RGB.svg";
import { Button } from "@/components/ui/button";
import { Menu, X, Award, Gift, Trophy, Calendar, Users, MessageSquare, FileText, PieChart, 
  Rocket, Compass, Lightbulb, Bot, Plug, ChevronDown, Video, FileSpreadsheet, Bell, ChevronRight } from "lucide-react";
import { DemoBookingModal } from "./DemoBookingModal";
import { LazyImage } from "./ui/lazy-image";
import { usePostHog } from "@/hooks/usePostHog";

// Simple link component for SSR compatibility
const NavLink = ({ to, children, className, onClick, title, ...props }: { 
  to: string; 
  children: React.ReactNode; 
  className?: string; 
  onClick?: () => void;
  title?: string;
  [key: string]: any;
}) => {
  return (
    <a 
      href={to} 
      className={className} 
      onClick={onClick}
      title={title}
      {...props}
    >
      {children}
    </a>
  );
};

const Navbar = () => {
  const { posthog } = usePostHog();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [activeSecondary, setActiveSecondary] = useState<string | null>(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [showDemoButton, setShowDemoButton] = useState(false);
  const [showNavShadow, setShowNavShadow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Scroll detection for demo button animation and navbar shadow
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight * 0.8; // Approximate hero section height
      const scrolled = window.scrollY > heroHeight;
      const hasScrolled = window.scrollY > 80; // Show solid background after 80px scroll
      setShowDemoButton(scrolled);
      setShowNavShadow(hasScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial scroll position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Debug logging for mobile responsiveness issues
  useEffect(() => {
    const checkScreenSize = () => {
      console.log('Screen width:', window.innerWidth);
      console.log('Is mobile menu showing:', window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setActiveSecondary(null);
  };

  // Toggle dropdown for mobile menu
  const toggleDropdown = (dropdown: string) => {
    const newState = activeDropdown === dropdown ? null : dropdown;
    setActiveDropdown(newState);
    setActiveSecondary(null);
  };

  // Handle mouse enter for main menu items with delay to prevent flickering
  const handleMouseEnter = (dropdown: string) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoverTimeout(setTimeout(() => {
      setActiveDropdown(dropdown);
    }, 100));
  };

  // Handle mouse leave for main menu items with delay
  const handleMouseLeave = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoverTimeout(setTimeout(() => {
      setActiveDropdown(null);
      setActiveSecondary(null);
    }, 300));
  };

  const handleSecondaryHover = (category: string | null) => {
    setActiveSecondary(category);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
    setActiveSecondary(null);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDesktop = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideMobile = mobileMenuRef.current && !mobileMenuRef.current.contains(target);
      
      // Only close dropdowns if clicking outside both desktop and mobile menus
      if (isOutsideDesktop && isOutsideMobile) {
        setActiveDropdown(null);
        setActiveSecondary(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const productCategories = [
    {
      id: "recognition",
      title: "Recognition & Rewards",
      hasSecondary: true,
      items: [
        { icon: <Award className="w-5 h-5" />, title: "Peer-to-Peer Recognition", path: "/employee-recognition/peer-to-peer" },
        { icon: <Award className="w-5 h-5" />, title: "Awards", path: "/employee-recognition/awards" },
        { icon: <Calendar className="w-5 h-5" />, title: "Milestones", path: "/employee-recognition/milestones" },
        { icon: <Gift className="w-5 h-5" />, title: "Rewards", path: "/employee-recognition/rewards" },
        { icon: <Trophy className="w-5 h-5" />, title: "Challenges", path: "/employee-recognition/challenges" },
      ]
    },
    {
      id: "engagement",
      title: "Engagement",
      hasSecondary: true,
      items: [
        { icon: <MessageSquare className="w-5 h-5" />, title: "Announcements", path: "/employee-engagement/announcements" },
      ]
    }
  ];

  const solutionsItems = [
    { title: "Recognize What Matters", path: "/solutions/recognition" },
    { title: "Celebrate Culture Moments", path: "/solutions/culture" },
    { title: "Empower Every Manager", path: "/solutions/managers" },
    { title: "Turn Data Into Action", path: "/solutions/data-action" },
  ];

  const resourceItems = [
    { icon: <FileText className="w-5 h-5" />, title: "Recognition Guide", path: "/employee-recognition-program-guide-assembly/introduction" },
    { icon: <FileText className="w-5 h-5" />, title: "Blog", path: "/blog" },
    { icon: <Users className="w-5 h-5" />, title: "Case Studies", path: "/resources/case-studies" },
    { icon: <FileSpreadsheet className="w-5 h-5" />, title: "White Papers", path: "/resources/whitepapers" },
    { icon: <MessageSquare className="w-5 h-5" />, title: "Help Center", path: "help.joinassembly.com", external: true },
    { icon: <Video className="w-5 h-5" />, title: "Webinars", path: "/events" },
  ];

  // Simple menu item for sub-items
  const SubMenuItem = ({ icon, title, path }: { icon: React.ReactNode, title: string, path: string }) => {
    return (
      <NavLink 
        to={path} 
        className="flex items-center gap-3 p-3 rounded-md hover:bg-gray-50 transition-colors" 
        onClick={closeDropdown}
      >
        <div className="flex items-center gap-3">
          {icon && <div className="text-assembly-orange">{icon}</div>}
          <div className="text-sm font-medium">{title}</div>
        </div>
      </NavLink>
    );
  };

  return (
    <>
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${showNavShadow ? 'bg-white/80 backdrop-blur-md border-b border-white/20 shadow-sm' : 'bg-transparent backdrop-blur-sm'}`}>
      <div className="lg:container lg:mx-auto lg:2xl:px-12 lg:max-w-none lg:2xl:max-w-[1600px]">
        <div className="px-4 sm:px-6 lg:px-8 xl:px-16 2xl:px-20">
          <div className="flex items-center justify-between h-16 lg:h-[4.5rem] 2xl:h-20">
            <div className="flex items-center">
              <NavLink to="/" className="flex-shrink-0 flex items-center" aria-label="Assembly Home">
                <img 
                  src={assemblyLogo}
                  alt="Assembly by Quantum Workplace Logo" 
                  className="h-11 lg:h-14"
                  loading="eager"
                />
              </NavLink>
              
              <nav className="hidden lg:flex lg:ml-4 xl:ml-10 2xl:ml-20 items-center space-x-3 lg:space-x-4 xl:space-x-8 2xl:space-x-16" ref={dropdownRef}>
                {/* Product Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => handleMouseEnter('product')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`flex items-center text-gray-800 hover:text-gray-900 text-xs lg:text-sm font-medium ${activeDropdown === 'product' ? 'text-gray-900' : ''}`}
                    aria-expanded={activeDropdown === 'product'}
                    aria-controls="product-dropdown"
                  >
                    <span>Product</span>
                    <ChevronDown className={`ml-1 h-4 w-4 lg:h-5 lg:w-5 2xl:h-6 2xl:w-6 transition-transform duration-200 ${activeDropdown === 'product' ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  
                  {activeDropdown === 'product' && (
                    <div 
                      id="product-dropdown"
                      className="absolute left-0 mt-2 bg-white shadow-lg rounded-md overflow-hidden z-50 flex"
                      onMouseEnter={() => clearTimeout(hoverTimeout)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="w-64 lg:w-72 2xl:w-80 py-3 px-2">
                        {productCategories.map((category) => (
                          <div 
                            key={category.id}
                            onMouseEnter={() => category.hasSecondary ? handleSecondaryHover(category.id) : handleSecondaryHover(null)}
                            className={`flex items-center justify-between py-3 px-4 rounded-md ${activeSecondary === category.id ? 'bg-gray-100' : 'hover:bg-gray-50'} transition-colors cursor-pointer`}
                          >
                            <span className="text-sm lg:text-base 2xl:text-lg font-medium">{category.title}</span>
                            {category.hasSecondary && <ChevronRight className="h-4 w-4 lg:h-5 lg:w-5 2xl:h-6 2xl:w-6" />}
                          </div>
                        ))}
                      </div>
                      
                      {activeSecondary && (
                        <div className="w-64 lg:w-72 2xl:w-80 py-3 px-2 bg-white border-l border-gray-100">
                          {productCategories.find(c => c.id === activeSecondary)?.items?.map((item, idx) => (
                            <NavLink 
                              key={idx}
                              to={item.path}
                              title={`Learn about ${item.title.toLowerCase()}`}
                              className="flex items-center gap-3 py-3 px-4 rounded-md hover:bg-gray-50 transition-colors"
                              onClick={closeDropdown}
                            >
                              <div className="text-assembly-orange">{item.icon}</div>
                              <span className="text-sm lg:text-base 2xl:text-lg font-medium">{item.title}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Solutions Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => handleMouseEnter('solutions')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`flex items-center text-gray-800 hover:text-gray-900 text-xs lg:text-sm font-medium ${activeDropdown === 'solutions' ? 'text-gray-900' : ''}`}
                    aria-expanded={activeDropdown === 'solutions'}
                    aria-controls="solutions-dropdown"
                  >
                    <span>Solutions</span>
                    <ChevronDown className={`ml-1 h-4 w-4 lg:h-5 lg:w-5 2xl:h-6 2xl:w-6 transition-transform duration-200 ${activeDropdown === 'solutions' ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  
                  {activeDropdown === 'solutions' && (
                    <div 
                      id="solutions-dropdown"
                      className="absolute left-0 mt-2 w-[350px] lg:w-[400px] 2xl:w-[450px] bg-white shadow-lg rounded-md overflow-hidden z-50"
                      onMouseEnter={() => clearTimeout(hoverTimeout)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="p-6 lg:p-8 2xl:p-10">
                        <div className="space-y-4 lg:space-y-5 2xl:space-y-6">
                          {solutionsItems.map((item, idx) => (
                            <NavLink 
                              key={idx}
                              to={item.path} 
                              title={`Discover how Assembly helps you ${item.title.toLowerCase()}`}
                              className="block text-sm lg:text-base 2xl:text-lg text-gray-700 hover:text-assembly-orange py-2" 
                              onClick={closeDropdown}
                            >
                              {item.title}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Resources Dropdown */}
                <div 
                  className="relative"
                  onMouseEnter={() => handleMouseEnter('resources')}
                  onMouseLeave={handleMouseLeave}
                >
                  <button
                    className={`flex items-center text-gray-800 hover:text-gray-900 text-xs lg:text-sm font-medium ${activeDropdown === 'resources' ? 'text-gray-900' : ''}`}
                    aria-expanded={activeDropdown === 'resources'}
                    aria-controls="resources-dropdown"
                  >
                    <span>Resources</span>
                    <ChevronDown className={`ml-1 h-4 w-4 lg:h-5 lg:w-5 2xl:h-6 2xl:w-6 transition-transform duration-200 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  
                  {activeDropdown === 'resources' && (
                    <div 
                      id="resources-dropdown"
                      className="absolute left-0 mt-2 w-[300px] lg:w-[350px] 2xl:w-[400px] bg-white shadow-lg rounded-md overflow-hidden z-50"
                      onMouseEnter={() => clearTimeout(hoverTimeout)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="py-3 px-2 lg:py-4 lg:px-3 2xl:py-5 2xl:px-4">
                        <div className="space-y-1 lg:space-y-2">
                          {resourceItems.map((item, idx) => (
                            item.external ? (
                              <a
                                key={idx}
                                href={`https://${item.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Visit Assembly's ${item.title.toLowerCase()}`}
                                className="flex items-center gap-3 py-3 px-4 lg:py-4 lg:px-5 2xl:py-5 2xl:px-6 rounded-md hover:bg-gray-50 transition-colors"
                                onClick={closeDropdown}
                              >
                                <div className="text-assembly-orange">{item.icon}</div>
                                <span className="text-sm lg:text-base 2xl:text-lg font-medium">{item.title}</span>
                              </a>
                            ) : (
                              <NavLink 
                                key={idx}
                                to={item.path}
                                title={`Access Assembly's ${item.title.toLowerCase()}`}
                                className="flex items-center gap-3 py-3 px-4 lg:py-4 lg:px-5 2xl:py-5 2xl:px-6 rounded-md hover:bg-gray-50 transition-colors"
                                onClick={closeDropdown}
                              >
                                <div className="text-assembly-orange">{item.icon}</div>
                                <span className="text-sm lg:text-base 2xl:text-lg font-medium">{item.title}</span>
                              </NavLink>
                            )
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pricing Link */}
                <NavLink 
                  to="/pricing" 
                  title="View Assembly pricing plans"
                  className="text-gray-800 hover:text-gray-900 text-xs lg:text-sm font-medium"
                >
                  Pricing
                </NavLink>
              </nav>
            </div>
            
            <div className="hidden lg:flex lg:items-center lg:space-x-2 xl:space-x-4">
              <Button 
                className="bg-[#2563EB] hover:bg-[#1E40AF] text-white text-xs lg:text-sm px-3 lg:px-5 xl:px-6 py-2 lg:py-2.5"
                onClick={() => {
                  posthog?.capture('book_demo_clicked', {
                    page: window.location.pathname,
                    section: 'navbar',
                    button_text: 'Book Demo',
                    url: window.location.href
                  });
                  setIsDemoModalOpen(true);
                }}
                aria-label="Book Demo"
              >
                Book Demo
              </Button>
              <Button 
                variant="outline" 
                className="border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#2563EB] text-xs lg:text-sm px-4 lg:px-6 py-2 lg:py-2.5"
                onClick={() => window.open('https://app.joinassembly.com/login', '_blank')}
                aria-label="Sign In"
              >
                Sign In
              </Button>
            </div>
            
            {/* Mobile Menu Toggle Button */}
            <div className="lg:hidden flex">
              <button
                onClick={() => {
                  console.log('Mobile menu toggle clicked. Current state:', isMenuOpen);
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                <span className="sr-only">{isMenuOpen ? "Close main menu" : "Open main menu"}</span>
                {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-100" id="mobile-menu" ref={mobileMenuRef}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <div className="p-4 border-b">
              <button 
                className="font-medium flex items-center w-full text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleDropdown('mobile-product');
                }}
              >
                <span>Product</span>
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-product' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'mobile-product' && (
                <div className="mt-2 pl-2 space-y-3">
                  {productCategories.map((category, index) => (
                    <div key={index} className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-900">{category.title}</h3>
                       <ul className="space-y-1 pl-2">
                         {category.items.map((item, idx) => (
                           <li key={idx} className="p-2">
                             <NavLink to={item.path} title={`Learn about ${item.title.toLowerCase()}`} className="flex items-center" onClick={() => setIsMenuOpen(false)}>
                               <span className="text-assembly-orange mr-2">{item.icon}</span>
                               <span className="text-sm">{item.title}</span>
                             </NavLink>
                           </li>
                         ))}
                       </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-b">
              <button 
                className="font-medium flex items-center w-full text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleDropdown('mobile-solutions');
                }}
              >
                <span>Solutions</span>
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-solutions' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'mobile-solutions' && (
                <div className="mt-2 pl-2 space-y-3">
                  <div className="space-y-2">
                     <ul className="space-y-1 pl-2">
                       {solutionsItems.map((item, idx) => (
                         <li key={idx} className="p-2">
                           <NavLink to={item.path} title={`Discover how Assembly helps you ${item.title.toLowerCase()}`} className="text-sm" onClick={() => setIsMenuOpen(false)}>
                             {item.title}
                           </NavLink>
                         </li>
                       ))}
                     </ul>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-b">
              <button 
                className="font-medium flex items-center w-full text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleDropdown('mobile-resources');
                }}
              >
                <span>Resources</span>
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${activeDropdown === 'mobile-resources' ? 'rotate-180' : ''}`} />
              </button>
              {activeDropdown === 'mobile-resources' && (
                <div className="mt-2 pl-2 space-y-2">
                   <ul className="space-y-1 pl-2">
                     {resourceItems.map((item, idx) => (
                       <li key={idx} className="p-2">
                         {item.external ? (
                           <a href={`https://${item.path}`} target="_blank" rel="noopener noreferrer" title={`Visit Assembly's ${item.title.toLowerCase()}`} className="flex items-center text-sm" onClick={() => setIsMenuOpen(false)}>
                             <span className="text-assembly-orange mr-2">{item.icon}</span>
                             <span>{item.title}</span>
                           </a>
                         ) : (
                           <NavLink to={item.path} title={`Access Assembly's ${item.title.toLowerCase()}`} className="flex items-center text-sm" onClick={() => setIsMenuOpen(false)}>
                             <span className="text-assembly-orange mr-2">{item.icon}</span>
                             <span>{item.title}</span>
                           </NavLink>
                         )}
                       </li>
                     ))}
                   </ul>
                </div>
              )}
            </div>
            
            <div className="p-4 border-b">
              <NavLink to="/pricing" title="View Assembly pricing plans" className="font-medium" onClick={() => setIsMenuOpen(false)}>
                Pricing
              </NavLink>
            </div>
            
            <div className="p-4 space-y-3">
              <Button 
                variant="outline" 
                className="w-full border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF]"
                onClick={() => window.open('https://app.joinassembly.com/login', '_blank')}
              >
                Sign In
              </Button>
              <Button 
                onClick={() => {
                  setIsDemoModalOpen(true);
                  setIsMenuOpen(false);
                }}
                className="w-full bg-[#2563EB] hover:bg-[#1E40AF] text-white"
              >
                Book Demo
              </Button>
            </div>
          </div>
        </div>
      )}

      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </header>
    </>
  );
};

export default Navbar;